import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { TooltipWrapper } from 'components/TooltipWrapper';
import type { BimWorkOrderPriceData, ContractStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { BimChapterEnum, DeviationEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import {
    bimNumberFormatter,
    CodeCellRendererComponent,
    SmrGroupHeaderComponent,
    SmrTotalHeaderComponent,
} from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers';
import { IconAlert } from 'uikit';
import { formatNumberToRu } from 'utils';

import type { CellClassParams, ColDef, ColGroupDef, IHeaderParams, ValueFormatterParams } from 'ag-grid-community';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { BimWorkOrderRowTypeEnum, bimWorkOrderTotalsChapterLabel } from '../../types';
import {
    bimWorkOrderPriceCodeFilterGql,
    bimWorkOrderPriceNameFilterGql,
    bimWorkOrderPriceObjectNameFilterGql,
    bimWorkOrderPriceRowCodeFilterGql,
    bimWorkOrderPriceRowNameFilterGql,
} from '../bimWorkOrderPriceQueries';
import type { IBimWorkOrderRowDetails } from '../cellRenderers';
import { cellWithDifferentFiled } from '../cellRenderers';

import { useWorkOrderMenuColumn } from './useWorkOrderMenuColumn';

const rightAlignedCellClassName = 'ag-right-aligned-cell';

export interface ITotalFieldParams {
    leftSideGetter?: ((data: any) => React.ReactNode) | undefined;
    HeaderComponent?: (props: IHeaderParams) => JSX.Element;
}

export const TotalHeaderComponent = ({ displayName }: IHeaderParams) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <TooltipWrapper message="Превышает план">
            <IconAlert size="xs" view="alert" style={{ marginRight: 5 }} />
        </TooltipWrapper>
        {displayName}
    </div>
);
TotalHeaderComponent.displayName = 'TotalHeaderComponent';

export const totalLeftSideGetter = (data: any) => {
    if (data?.completedTotalDeviation === DeviationEnum.Up) {
        return <span style={{ color: 'var(--color-typo-alert)', marginRight: 5 }}>▲</span>;
    }

    return undefined;
};

const cellClass = (params: any) =>
    params.data?.isInactive ? ['inactive-act', rightAlignedCellClassName] : rightAlignedCellClassName;

const quantityFormatter = (p: ValueFormatterParams) => {
    const { value, data } = p;

    if (!value) {
        return '';
    }

    if (data?.isLimitPrice) {
        return bimNumberFormatter(3)({ value });
    }
    if (data?.name?.includes('в т.ч')) {
        return `${formatNumberToRu(value, 2)}%`;
    }
    if (data?.name?.includes('инфляции на')) {
        return `${formatNumberToRu(value, 4)}`;
    }

    return bimNumberFormatter(2)({ value });
};

export const getWorkOrderValueFields = (
    valuesKey: string,
    workOrderStatus?: WorkOrderStatusEnum,
    marryChildren = true,
    totalFieldParams?: ITotalFieldParams,
) => {
    const isWorkOrderInProgress = workOrderStatus === WorkOrderStatusEnum.InProgress;

    const keys = {
        smrTotal: `${valuesKey}.smrTotal`,
        ozp: `${valuesKey}.ozp`,
        emm: `${valuesKey}.emm`,
        including: `${valuesKey}.including`,
        zpm: `${valuesKey}.zpm`,
        mp: `${valuesKey}.mp`,
        nr: `${valuesKey}.nr`,
        sp: `${valuesKey}.sp`,
        op: `${valuesKey}.op`,
        others: `${valuesKey}.others`,
        total: `${valuesKey}.total`,
        mz: `${valuesKey}.mz`,
        oz: `${valuesKey}.oz`,
        tzr: `${valuesKey}.tzr`,
    };

    return [
        {
            headerName: 'Стоимость строительно-монтажных работ',
            headerGroupComponent: SmrGroupHeaderComponent,
            width: 600,
            children: [
                {
                    headerName: 'Всего СМР',
                    field: keys.smrTotal,
                    width: 140,
                    spanHeaderHeight: true,
                    valueFormatter: bimNumberFormatter(0),
                    cellRenderer: cellWithDifferentFiled('smrTotal', keys.smrTotal),
                    headerComponent: SmrTotalHeaderComponent,
                    headerComponentParams: {
                        workOrderStatus,
                        collapseKeys: [keys.ozp, keys.emm, keys.including, keys.zpm, keys.mp, keys.nr, keys.sp],
                    },
                    cellClass,
                },
                {
                    headerName: 'ОЗП',
                    field: keys.ozp,
                    width: 120,
                    spanHeaderHeight: true,
                    valueFormatter: bimNumberFormatter(0),
                    cellRenderer: cellWithDifferentFiled('ozp', keys.ozp),
                    hide: isWorkOrderInProgress,
                    cellClass,
                },
                {
                    headerName: 'ЭММ',
                    field: keys.emm,
                    width: 120,
                    spanHeaderHeight: true,
                    valueFormatter: bimNumberFormatter(0),
                    cellRenderer: cellWithDifferentFiled('emm', keys.emm),
                    hide: isWorkOrderInProgress,
                    cellClass,
                },
                {
                    headerName: 'В том числе',
                    field: keys.including,
                    width: 120,
                    spanHeaderHeight: true,
                    hide: isWorkOrderInProgress,
                    children: [
                        {
                            headerName: 'ЗПМ',
                            field: keys.zpm,
                            width: 180,
                            valueFormatter: bimNumberFormatter(0),
                            cellRenderer: cellWithDifferentFiled('zpm', keys.zpm),
                            hide: isWorkOrderInProgress,
                            cellClass,
                        },
                    ],
                    marryChildren,
                },
                {
                    headerName: 'МП',
                    field: keys.mp,
                    width: 120,
                    spanHeaderHeight: true,
                    valueFormatter: bimNumberFormatter(0),
                    cellRenderer: cellWithDifferentFiled('mp', keys.mp),
                    hide: isWorkOrderInProgress,
                    cellClass,
                },
                {
                    headerName: 'НР',
                    field: keys.nr,
                    width: 120,
                    spanHeaderHeight: true,
                    valueFormatter: bimNumberFormatter(0),
                    cellRenderer: cellWithDifferentFiled('nr', keys.nr),
                    hide: isWorkOrderInProgress,
                    cellClass,
                },
                {
                    headerName: 'СП',
                    field: keys.sp,
                    width: 120,
                    spanHeaderHeight: true,
                    valueFormatter: bimNumberFormatter(0),
                    cellRenderer: cellWithDifferentFiled('sp', keys.sp),
                    hide: isWorkOrderInProgress,
                    cellClass,
                },
            ],
            marryChildren,
        },
        {
            headerName: 'ОП',
            field: keys.op,
            width: 120,
            spanHeaderHeight: true,
            valueFormatter: bimNumberFormatter(0),
            cellRenderer: cellWithDifferentFiled('op', keys.op),
            cellClass,
        },
        {
            headerName: 'Прочие затраты',
            field: keys.others,
            width: 120,
            spanHeaderHeight: true,
            valueFormatter: bimNumberFormatter(0),
            cellRenderer: cellWithDifferentFiled('others', keys.others),
            cellClass,
        },
        {
            headerName: 'Всего',
            field: keys.total,
            width: 120,
            spanHeaderHeight: true,
            valueFormatter: bimNumberFormatter(0),
            cellRenderer: cellWithDifferentFiled('total', keys.total, totalFieldParams?.leftSideGetter),
            cellClass,
            headerComponent: totalFieldParams?.HeaderComponent,
        },
        {
            headerName: 'Справочно МЗ',
            field: keys.mz,
            width: 140,
            spanHeaderHeight: true,
            valueFormatter: bimNumberFormatter(0),
            cellRenderer: cellWithDifferentFiled('mz', keys.mz),
            cellClass,
        },
        {
            headerName: 'Справочно ОЗ',
            field: keys.oz,
            width: 140,
            spanHeaderHeight: true,
            valueFormatter: bimNumberFormatter(0),
            cellRenderer: cellWithDifferentFiled('oz', keys.oz),
            cellClass,
        },
        {
            headerName: 'ТЗР, чел.час',
            field: keys.tzr,
            width: 120,
            spanHeaderHeight: true,
            valueFormatter: bimNumberFormatter(2),
            cellRenderer: cellWithDifferentFiled('tzr', keys.tzr),
            cellClass,
        },
    ];
};

export const useWorkOrderColumns = ({
    workOrderId,
    workOrderRootObjectId,
    chapter,
    totalChapterDetailsRowOpenOuter,
    toggleTotalChapterDetailsOuter,
    makeRefreshTable,
    workOrderStatus,
    statusProject,
    contractStatus,
    hasExcessCompletedTotalSMR,
    orderRules,
    hasInProgress,
    isFrameContract,
}: {
    workOrderId: number;
    workOrderRootObjectId: number | undefined;
    chapter: BimChapterEnum | undefined;
    totalChapterDetailsRowOpenOuter?: { rowType: BimWorkOrderRowTypeEnum.TotalsString; data: null } | null;
    toggleTotalChapterDetailsOuter: () => void;
    makeRefreshTable: () => void;
    hasExcessCompletedTotalSMR: boolean;
    hasInProgress: boolean;
    isFrameContract: boolean;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
    statusProject?: ProjectStatusEnum;
    orderRules?: IUseAccess['orderBim'];
}) => {
    const [selection, , , setSelection] = useSelection('bimWorkOrderPriceTable');
    const [pinnedItems, , , setPinned] = usePin('bimWorkOrderPriceTable');

    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
    const [regularDetailsFilterValues, setRegularDetailsFilterValues] = useState<Record<string, string[]>>({});

    const { menuColumn, checkedPriceRowForDetails, closeDetailsModalAndCleanChecked, resetCheckedRows, checkedRows } =
        useWorkOrderMenuColumn({
            makeRefreshTable,
            workOrderStatus,
            contractStatus,
            orderRules,
            statusProject,
            hasInProgress,
        });

    const isWorkOrderOnFormation = useMemo(
        () => workOrderStatus === WorkOrderStatusEnum.OnFormation,
        [workOrderStatus],
    );
    const isWorkOrderInProgress = useMemo(() => workOrderStatus === WorkOrderStatusEnum.InProgress, [workOrderStatus]);

    const isTotalDetails = useMemo(
        () =>
            (checkedPriceRowForDetails || totalChapterDetailsRowOpenOuter)?.rowType ===
            BimWorkOrderRowTypeEnum.TotalsString,
        [checkedPriceRowForDetails, totalChapterDetailsRowOpenOuter],
    );

    const variables = useMemo(
        () => ({
            workOrderId,
            workOrderRootObjectId,
            chapter,
        }),
        [chapter, workOrderId, workOrderRootObjectId],
    );

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        const splitNames = fieldName.split('.');
        const keyName = splitNames[0] === 'object' ? splitNames[0] : splitNames[1];

        setFilterValues((prevValue) => ({
            ...prevValue,
            [keyName]: checkedValues,
        }));
    }, []);

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    `${chapter}_${workOrderRootObjectId}`,
                    getDefaultFilterCleaner({
                        changeFilterHandler,
                        setFiltersWhere: setFilterValues,
                    }),
                ),
            }),
            [changeFilterHandler, chapter, workOrderRootObjectId],
        ),
    );

    const changeRegularDetailsFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        const splitNames = fieldName.split('.');
        const keyName = splitNames[0] === 'object' ? splitNames[0] : splitNames[1];

        setRegularDetailsFilterValues((prevValue) => ({
            ...prevValue,
            [keyName]: checkedValues,
        }));
    }, []);

    const subObjectColumn = useMemo<ColDef<BimWorkOrderPriceData>>(
        () => ({
            headerName: 'Подобъект',
            field: 'object.name',
            width: 230,
            spanHeaderHeight: true,
            tooltipComponent: AgGridCellTooltipComponent,
            tooltipValueGetter: AgGridCellTooltipValueGetter({}),
            valueFormatter: (props) => {
                const { data, value } = props;

                if (data?.isLimitPrice) {
                    return '';
                }

                return value ?? '';
            },
            filter: AgGridCustomFilterAdapter,
            filterParams: {
                variables,
                changeFilterHandler,
                dataAccessor: 'bimWorkOrderPriceObjectNameFilter',
                whereVariables: ['value'],
                childFieldAccessor: 'value',
                columns: [
                    {
                        headerName: 'Подобъект',
                        field: 'value',
                        checkboxSelection: true,
                        cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                        width: 400,
                    },
                ],
                query: bimWorkOrderPriceObjectNameFilterGql,
            },
            lockPosition: 'left',
        }),
        [changeFilterHandler, variables],
    );

    const baseColumns = useMemo<ColDef<BimWorkOrderPriceData>[]>(
        () => [
            {
                headerName: 'Шифр',
                field: 'planValue.code',
                width: 230,
                spanHeaderHeight: true,
                suppressMovable: true,
                cellRenderer: CodeCellRendererComponent,
                cellRendererParams: {
                    field: 'name',
                    dotNotationField: ['planValue.code', 'completedValue.code'],
                },
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'bimWorkOrderPriceCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                            width: 400,
                        },
                    ],
                    query: bimWorkOrderPriceCodeFilterGql,
                },
                lockPosition: 'left',
            },
            {
                headerName: 'Наименование работ, затрат',
                field: 'planValue.name',
                maxWidth: 600,
                minWidth: 180,
                flex: 1,
                spanHeaderHeight: true,
                suppressMovable: true,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'bimWorkOrderPriceNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование работ, затрат',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                            width: 400,
                        },
                    ],
                    query: bimWorkOrderPriceNameFilterGql,
                },
                lockPosition: 'left',
            },
            {
                headerName: 'Ед.изм',
                field: 'planValue.unit',
                width: 100,
                spanHeaderHeight: true,
            },
            {
                headerName: 'Кол-во',
                field: 'planValue.quantity',
                width: 100,
                spanHeaderHeight: true,
                valueFormatter: (props) => {
                    const { data, value } = props;

                    if (!value) {
                        return '';
                    }

                    if (data?.isLimitPrice) {
                        return bimNumberFormatter(3)({ value });
                    }

                    return bimNumberFormatter(2)({ value });
                },
                cellClass: rightAlignedCellClassName,
            },
        ],
        [changeFilterHandler, variables],
    );

    const baseColumnsDetails = useMemo<ColDef<BimWorkOrderPriceData>[]>(
        () => [
            {
                headerName: 'Шифр',
                field: 'planValue.code',
                width: 230,
                spanHeaderHeight: true,
                suppressMovable: !isTotalDetails,
                cellRenderer: CodeCellRendererComponent,
                cellRendererParams: {
                    field: 'code',
                    dotNotationField: 'planValue.code',
                },
            },
            {
                headerName: 'Наименование работ, затрат',
                field: 'planValue.name',
                width: 180,
                spanHeaderHeight: true,
                suppressMovable: !isTotalDetails,
            },
            {
                headerName: 'Ед.изм',
                field: 'planValue.unit',
                width: 100,
                spanHeaderHeight: true,
                cellClass: rightAlignedCellClassName,
            },
            {
                headerName: 'Кол-во',
                field: 'planValue.quantity',
                width: 100,
                spanHeaderHeight: true,
                valueFormatter: quantityFormatter,
                cellClass: rightAlignedCellClassName,
            },
        ],
        [isTotalDetails],
    );

    const isUseInProgressColumnsSetup = useMemo(
        () =>
            isWorkOrderInProgress ||
            workOrderStatus === WorkOrderStatusEnum.Completed ||
            (workOrderStatus === WorkOrderStatusEnum.Delayed && hasInProgress),
        [hasInProgress, isWorkOrderInProgress, workOrderStatus],
    );

    const fullSetBaseColumns = useMemo<(ColDef | ColGroupDef)[]>(
        () => [
            menuColumn,
            subObjectColumn,
            ...baseColumns,
            ...(isUseInProgressColumnsSetup
                ? [
                      {
                          headerName: `План по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                          children: getWorkOrderValueFields('planValue', workOrderStatus),
                          marryChildren: true,
                      },
                      {
                          headerName: `Выполнено по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                          children: getWorkOrderValueFields('completedValue', workOrderStatus, true, {
                              HeaderComponent: hasExcessCompletedTotalSMR ? TotalHeaderComponent : undefined,
                              leftSideGetter: totalLeftSideGetter,
                          }),
                          marryChildren: true,
                      },
                      {
                          headerName: `Остаток по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                          children: getWorkOrderValueFields('remainderValue', workOrderStatus),
                          marryChildren: true,
                      },
                  ]
                : getWorkOrderValueFields('planValue', workOrderStatus)),
        ],
        [
            baseColumns,
            hasExcessCompletedTotalSMR,
            isFrameContract,
            isUseInProgressColumnsSetup,
            menuColumn,
            subObjectColumn,
            workOrderStatus,
        ],
    );

    const detailsBaseColumns = useMemo(
        () => [...baseColumnsDetails, ...getWorkOrderValueFields('planValue', undefined, !isTotalDetails)],
        [baseColumnsDetails, isTotalDetails],
    );

    const fullSetSettingsColumns = useMemo(
        () =>
            fullSetBaseColumns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 4) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [fullSetBaseColumns, pinnedItems],
    );

    const isFullSetColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const onFullSetTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setSelection, setPinned],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns: fullSetBaseColumns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isFullSetColumnVisible(getColumnId(column)));
    }, [fullSetBaseColumns, isFullSetColumnVisible, pinnedItems]);

    const detailsColumns = useMemo(
        () =>
            detailsBaseColumns.map((column) => {
                if (column.field?.includes('name')) {
                    return {
                        ...column,
                        wrapText: true,
                        autoHeight: true,
                        width: 400,
                        cellClassRules: {
                            topPinnedMenuCell: (params: CellClassParams) => params.node.rowPinned === 'top',
                            wrappedCenteredCell: (params: CellClassParams) => !params.node.rowPinned,
                        },
                    };
                }

                return column;
            }),
        [detailsBaseColumns],
    );

    const recursionHelper = useCallback(
        (arr: any[]): any[] =>
            arr.map((c) => {
                let child;

                if (c.children) {
                    child = recursionHelper(c.children);
                }

                return {
                    ...c,
                    ...(c.field?.includes('name') && { field: 'name' }),
                    cellClassRules: {
                        ...c.cellClassRules,
                        boldWeight: (params: CellClassParams) => !params.colDef.field?.includes('name'),
                    },
                    valueFormatter: (p: ValueFormatterParams) => {
                        const { value, data } = p;

                        if (data?.name?.includes('Индексы')) {
                            return formatNumberToRu(value, 2);
                        }

                        return formatNumberToRu(value, 0);
                    },
                    ...(child && { children: child }),
                    suppressMovable: true,
                };
            }),
        [],
    );

    const detailsColumnsByType = useMemo<ColDef[]>(() => {
        if (isTotalDetails) {
            const mappedTotalDetailsColumns = recursionHelper(
                detailsColumns.filter(
                    (_, idx) =>
                        idx === 1 ||
                        (chapter === BimChapterEnum.Chapter12 || chapter === BimChapterEnum.Chapter9
                            ? idx > 2
                            : idx > 3),
                ),
            ).map((c) => {
                if (c.field === 'planValue.quantity') {
                    return {
                        ...c,
                        field: 'quantity',
                        valueFormatter: quantityFormatter,
                        cellClass: 'secondaryRow',
                        cellClassRules: {
                            ...c.cellClassRules,
                            boldWeight: false,
                        },
                    };
                }

                return c;
            });

            return [
                ...(isUseInProgressColumnsSetup
                    ? [
                          mappedTotalDetailsColumns[0],
                          {
                              headerName: `План по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                              children: mappedTotalDetailsColumns.slice(1),
                          },
                      ]
                    : mappedTotalDetailsColumns),
                {
                    headerName: `Выполнено по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                    field: 'completedValue.total',
                    spanHeaderHeight: true,
                    valueFormatter: bimNumberFormatter(0),
                    cellClass: `${rightAlignedCellClassName} boldWeight`,
                    hide: isWorkOrderOnFormation,
                    width: 150,
                },
                {
                    headerName: `Остаток ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                    field: 'remainderValue.total',
                    spanHeaderHeight: true,
                    valueFormatter: bimNumberFormatter(0),
                    cellClass: `${rightAlignedCellClassName} boldWeight`,
                    hide: isWorkOrderOnFormation,
                    width: 150,
                },
            ];
        }

        return detailsColumns.map((column) => {
            if (column.headerName === 'Шифр' && checkedPriceRowForDetails?.data) {
                return {
                    ...column,
                    cellRenderer: undefined,
                    filter: AgGridCustomFilterAdapter,
                    filterParams: {
                        variables: {
                            workOrderPriceId: (
                                checkedPriceRowForDetails as IBimWorkOrderRowDetails<BimWorkOrderPriceData>
                            ).data.id,
                        },
                        changeFilterHandler: changeRegularDetailsFilterHandler,
                        dataAccessor: 'bimWorkOrderPriceRowCodeFilter',
                        whereVariables: ['value'],
                        childFieldAccessor: 'value',
                        columns: [
                            {
                                headerName: 'Шифр',
                                field: 'value',
                                checkboxSelection: true,
                                cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                                width: 400,
                            },
                        ],
                        query: bimWorkOrderPriceRowCodeFilterGql,
                    },
                    lockPosition: 'left',
                };
            }

            if (column.headerName === 'Наименование работ, затрат' && checkedPriceRowForDetails?.data) {
                return {
                    ...column,
                    cellRenderer: undefined,
                    filter: AgGridCustomFilterAdapter,
                    filterParams: {
                        variables: {
                            workOrderPriceId: (
                                checkedPriceRowForDetails as IBimWorkOrderRowDetails<BimWorkOrderPriceData>
                            ).data.id,
                        },
                        changeFilterHandler: changeRegularDetailsFilterHandler,
                        dataAccessor: 'bimWorkOrderPriceRowNameFilter',
                        whereVariables: ['value'],
                        childFieldAccessor: 'value',
                        columns: [
                            {
                                headerName: 'Наименование работ, затрат',
                                field: 'value',
                                checkboxSelection: true,
                                cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                                width: 400,
                            },
                        ],
                        query: bimWorkOrderPriceRowNameFilterGql,
                    },
                    lockPosition: 'left',
                };
            }

            return column;
        });
    }, [
        recursionHelper,
        detailsColumns,
        isTotalDetails,
        isUseInProgressColumnsSetup,
        isFrameContract,
        isWorkOrderOnFormation,
        chapter,
        checkedPriceRowForDetails,
        changeRegularDetailsFilterHandler,
    ]);

    const detailsTitle = useMemo(() => {
        if (isTotalDetails) {
            return `итоги ${
                chapter ? bimWorkOrderTotalsChapterLabel[chapter as keyof typeof bimWorkOrderTotalsChapterLabel] : ''
            }`;
        }

        return 'состав сметы';
    }, [chapter, isTotalDetails]);

    const closeDetailsModalAndCleanCheckedWithOuter = useCallback(() => {
        if (totalChapterDetailsRowOpenOuter) {
            toggleTotalChapterDetailsOuter();
        } else {
            closeDetailsModalAndCleanChecked();
            setRegularDetailsFilterValues({});
        }
    }, [closeDetailsModalAndCleanChecked, toggleTotalChapterDetailsOuter, totalChapterDetailsRowOpenOuter]);

    return {
        fullSetColumns,
        detailsColumnsByType,
        detailsTitle,
        checkedPriceRowForDetails,
        closeDetailsModalAndCleanChecked: closeDetailsModalAndCleanCheckedWithOuter,
        fullSetSettingsColumns,
        onFullSetTableSettingsConfirm,
        isFullSetColumnVisible,
        resetCheckedRows,
        checkedRows,
        filterValues,
        regularDetailsFilterValues,
        isTotalDetails,
    };
};
